"use client";

import React, { createContext, useContext, useEffect } from "react";
import posthog from "posthog-js";
import { useAuth } from "@/contexts/AuthContext";

export interface PropertyViewedPayload {
  property_id: string;
  property_type?: string;
  listing_type?: string;
  location_city?: string;
  location_area?: string;
  price?: number;
  price_band?: "entry" | "mid" | "premium" | "luxury";
  bedrooms?: number;
  realtor_id?: string;
  developer_id?: string;
  is_managed_property?: boolean;
  traffic_source?: string;
  entry_source?: "direct" | "internal" | "external";
  result_position?: number;
}

export interface PropertySearchedPayload {
  search_term?: string;
  location?: string;
  property_type?: string;
  listing_type?: string;
  bedrooms?: number;
  min_budget?: number;
  max_budget?: number;
  results_count: number;
  sort?: string;
  filters_used?: string[];
}

export interface WhatsAppClickedPayload {
  property_id: string;
  realtor_id?: string;
  developer_id?: string;
  listing_type?: string;
  property_type?: string;
  location_city?: string;
  price?: number;
}

export interface DirectCallClickedPayload {
  property_id: string;
  realtor_id?: string;
  developer_id?: string;
  listing_type?: string;
  property_type?: string;
  location_city?: string;
  price?: number;
}

export interface InspectionClickedPayload {
  property_id: string;
  realtor_id?: string;
  developer_id?: string;
  listing_type?: string;
  property_type?: string;
  location_city?: string;
  price?: number;
}

export interface PropertySavedPayload {
  property_id: string;
  property_type?: string;
  listing_type?: string;
  location_city?: string;
  location_area?: string;
  price?: number;
  realtor_id?: string;
  save_action: "saved" | "unsaved";
}

export interface PropertyRequestSubmittedPayload {
  request_id: string;
  property_type?: string;
  listing_type?: string;
  location?: string;
  min_budget?: number;
  max_budget?: number;
  bedrooms?: number;
  urgency?: string;
  preferred_contact_channel?: string;
}

export interface ReviewSubmittedPayload {
  review_id: string;
  realtor_id: string;
  property_id?: string;
  rating: number;
  interaction_type?: string;
  review_source?: string;
  verified_interaction: boolean;
}

export interface ListingSubmittedPayload {
  listing_id: string;
  property_type?: string;
  listing_type?: string;
  location_city?: string;
  location_area?: string;
  price?: number;
  user_type?: string;
  realtor_id?: string;
  developer_id?: string;
  subscription_plan?: string;
}

export interface ListingApprovedPayload {
  listing_id: string;
  property_type?: string;
  listing_type?: string;
  location_city?: string;
  realtor_id?: string;
  developer_id?: string;
  approval_time?: string;
  review_duration_hours?: number;
  subscription_plan?: string;
}

export interface SubscriptionCompletedPayload {
  subscription_id: string;
  plan_name?: string;
  plan_type?: string;
  amount?: number;
  currency?: string;
  payment_method?: string;
  payment_reference?: string;
  billing_period?: string;
  user_type?: string;
  previous_plan?: string;
  current_plan?: string;
}

export interface InspectionPaymentCompletedPayload {
  inspection_id?: string;
  property_id: string;
  inspection_type?: string;
  amount?: number;
  currency?: string;
  payment_method?: string;
  payment_reference?: string;
  realtor_id?: string;
  location_city?: string;
}

export type FunnelEventPayload = Record<string, any>;

/**
 * Canonical analytics event names (see temp/posthog-tracking-plan.md).
 * Verified firing in the codebase (`live`) plus specified (`planned`) names
 * so new instrumentation is typo-proof from day one. The legacy untyped
 * `trackEvent(event: string)` remains for back-compat — prefer this.
 */
export type AnalyticsEventName =
  | "property_viewed"
  | "property_searched"
  | "search_results_viewed"
  | "whatsapp_clicked"
  | "direct_call_click"
  | "inspection_clicked"
  | "property_saved"
  | "property_request_submitted"
  | "review_submitted"
  | "listing_started"
  | "listing_step_completed"
  | "listing_submitted"
  | "listing_failed"
  | "listing_live"
  | "listing_policy_viewed"
  | "listing_policy_accepted"
  | "user_logged_in"
  | "user_signed_up"
  | "signup_started"
  | "email_verified"
  | "kyc_started"
  | "kyc_submitted"
  | "kyc_verified"
  | "kyc_rejected"
  | "realtor_upgrade_viewed"
  | "realtor_upgrade_initiated"
  | "realtor_upgrade_completed"
  | "realtor_onboarding_submitted"
  | "pricing_viewed"
  | "plan_chosen"
  | "checkout_started"
  | "payment_succeeded"
  | "plan_active"
  | "subscription_completed"
  | "inspection_payment_completed"
  | "user_daily_active"
  | "user_viewed_property"
  | "user_explored_properties"
  | "user_explored_managed_props"
  | "user_checked_weekly_deals"
  | "user_export"
  | "cookie_consent_decided"
  | "page_not_found"
  | "cta_clicked"
  | "filter_applied"
  | "form_started"
  | "form_submitted"
  | "error_shown";

interface PostHogAnalyticsContextType {
  trackPropertyViewed: (payload: PropertyViewedPayload) => void;
  trackPropertySearched: (payload: PropertySearchedPayload) => void;
  trackWhatsAppClicked: (payload: WhatsAppClickedPayload) => void;
  trackDirectCallClicked: (payload: DirectCallClickedPayload) => void;
  trackInspectionClicked: (payload: InspectionClickedPayload) => void;
  trackPropertySaved: (payload: PropertySavedPayload) => void;
  trackPropertyRequestSubmitted: (payload: PropertyRequestSubmittedPayload) => void;
  trackReviewSubmitted: (payload: ReviewSubmittedPayload) => void;
  trackListingSubmitted: (payload: ListingSubmittedPayload) => void;
  trackSubscriptionCompleted: (payload: SubscriptionCompletedPayload) => void;
  trackInspectionPaymentCompleted: (
    payload: InspectionPaymentCompletedPayload,
  ) => void;
  /** Generic consent-gated capture for funnel events (auth, KYC, listing steps). */
  trackEvent: (event: string, payload?: FunnelEventPayload) => void;
  /** Typed variant — prefer for all new instrumentation (see AnalyticsEventName). */
  trackAnalyticsEvent: (event: AnalyticsEventName, payload?: FunnelEventPayload) => void;
}

const noop = () => undefined;

/** Consent-first: PostHog is measurement, so the analytics flag gates it. */
function hasPostHogConsent(): boolean {
  try {
    if (typeof window === "undefined" || !window.localStorage) return false;
    const status = window.localStorage.getItem("fnp_cookie_consent");
    if (status === "accepted") return true;
    if (status !== "custom") return false;
    const cats = JSON.parse(
      window.localStorage.getItem("fnp_cookie_categories") ?? "{}",
    ) as { analytics?: boolean; marketing?: boolean };
    return cats.analytics === true;
  } catch {
    return false;
  }
}

function safeCapture(event: string, payload?: Record<string, any>): void {
  if (!hasPostHogConsent()) return;
  try {
    posthog.capture(event, payload);
  } catch {
    /* noop */
  }
}

const NOOP_CONTEXT: PostHogAnalyticsContextType = {
  trackPropertyViewed: noop,
  trackPropertySearched: noop,
  trackWhatsAppClicked: noop,
  trackDirectCallClicked: noop,
  trackInspectionClicked: noop,
  trackPropertySaved: noop,
  trackPropertyRequestSubmitted: noop,
  trackReviewSubmitted: noop,
  trackListingSubmitted: noop,
  trackSubscriptionCompleted: noop,
  trackInspectionPaymentCompleted: noop,
  trackEvent: noop,
  trackAnalyticsEvent: noop,
};

const PostHogAnalyticsContext = createContext<
  PostHogAnalyticsContextType | undefined
>(undefined);

const ANALYTICS: PostHogAnalyticsContextType = {
  trackPropertyViewed: (payload) => safeCapture("property_viewed", payload),
  trackPropertySearched: (payload) =>
    safeCapture("property_searched", payload),
  trackWhatsAppClicked: (payload) =>
    safeCapture("whatsapp_clicked", payload),
  trackDirectCallClicked: (payload) =>
    safeCapture("direct_call_click", payload),
  trackInspectionClicked: (payload) =>
    safeCapture("inspection_clicked", payload),
  trackPropertySaved: (payload) => safeCapture("property_saved", payload),
  trackPropertyRequestSubmitted: (payload) =>
    safeCapture("property_request_submitted", payload),
  trackReviewSubmitted: (payload) =>
    safeCapture("review_submitted", payload),
  trackListingSubmitted: (payload) =>
    safeCapture("listing_submitted", payload),
  trackSubscriptionCompleted: (payload) =>
    safeCapture("subscription_completed", payload),
  trackInspectionPaymentCompleted: (payload) =>
    safeCapture("inspection_payment_completed", payload),
  trackEvent: (event, payload) => safeCapture(event, payload),
  trackAnalyticsEvent: (event, payload) => safeCapture(event, payload),
};

const DAU_STAMP_KEY = "fnp_dau_last_sent";

function todayStamp(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

export function PostHogAnalyticsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, isDeveloper, isRealtor, status } = useAuth();

  useEffect(() => {
    if (status === "idle" || status === "loading") return;

    if (user?.id) {
      const userType = isDeveloper
        ? "developer"
        : isRealtor
          ? "realtor"
          : "buyer_renter";

      // identify() ships $anon_distinct_id, so PostHog merges pre-auth
      // browsing (same device) into this person — no explicit alias needed.
      posthog.identify(user.id, {
        email: user.email,
        user_type: userType,
        account_status: user.kyc_status || "pending",
      });
      // Segmentable on every event (before_send defaults it to anonymous).
      try {
        posthog.register({ auth_state: "authenticated" });
      } catch {
        /* noop */
      }

      // Daily active signed-in user heartbeat (consent-gated via safeCapture).
      try {
        if (window.localStorage.getItem(DAU_STAMP_KEY) !== todayStamp()) {
          safeCapture("user_daily_active", {
            user_type: userType,
            kyc_status: user.kyc_status || "pending",
          });
          window.localStorage.setItem(DAU_STAMP_KEY, todayStamp());
        }
      } catch {
        /* storage unavailable — heartbeat skipped */
      }
    } else {
      posthog.reset();
    }
  }, [user?.id, user?.email, user?.kyc_status, isDeveloper, isRealtor, status]);

  return (
    <PostHogAnalyticsContext.Provider value={ANALYTICS}>
      {children}
    </PostHogAnalyticsContext.Provider>
  );
}

export function usePostHogAnalytics(): PostHogAnalyticsContextType {
  const context = useContext(PostHogAnalyticsContext);
  if (!context) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "usePostHogAnalytics called outside PostHogAnalyticsProvider; events will be dropped.",
      );
    }
    return NOOP_CONTEXT;
  }
  return context;
}
